<template>
  <div>
    <v-row>
      <v-col cols="12"><h1 class="font-weight-thin">Extracts</h1></v-col>
    </v-row>
    <v-divider class="my-5"></v-divider>
    <v-card class="mt-2" elevation="0">
      <v-card-text>
        <v-autocomplete
          :items="packList"
          item-value="id"
          item-text="title"
          outlined
          placeholder="Choose a pack..."
          v-model="packId"
          data-cy="adminExtractForm__packSelect"
        >
          <template v-slot:item="{ item }">
            <div data-cy="adminExtractForm__packSelect--item">{{ item.title }}</div>
          </template>
        </v-autocomplete>
        <v-autocomplete
          :items="filteredExtractTypeList"
          item-value="id"
          item-text="name"
          outlined
          placeholder="Choose an extract type..."
          v-model="extractType"
          data-cy="adminExtractForm__extractTypeSelect"
        >
          <template v-slot:item="{ item }">
            <div data-cy="adminExtractForm__extractTypeSelect--item">{{ item.name }}</div>
          </template>
        </v-autocomplete>
        <v-btn
          :disabled="!(extractType && packId)"
          :href="extractType && packId ? `/api/extract/${extractType}/pack/${packId}` : undefined"
          data-cy="adminExtractForm__btn"
          color="primary"
          class="elevation-0"
          >Extract</v-btn
        >
      </v-card-text>
    </v-card>

    <v-divider class="my-5"></v-divider>

    <v-card class="mt-5" elevation="0">
      <v-card-title class="primary--text">Continuum CSV Extract Instructions</v-card-title>
      <v-card-text>
        <ol>
          <li>Select specific pack to extract from <span class="emphasized">Packs</span>.</li>
          <li>Select <span class="emphasized">Extract Type</span>.</li>
          <li>Click <span class="emphasized">Extract</span> button.</li>
          <li>Save downloaded extract.</li>
        </ol>
        <v-row>
          <v-col cols="12" md="10" lg="8">
            <div class="font-italic my-2">
              Note: Continuum extracts data into CSV files. In some cases, CSV files do not properly display certain
              characters, such as bullet points. OCHIN recommends importing the CSV file into Excel to view the extract
              properly.
            </div>
          </v-col>
        </v-row>

        <h3 class="mt-4 mb-2 primary--text">Importing CSV File into Excel (Windows 11)</h3>
        <ol>
          <li>Open a NEW Excel spreadsheet.</li>
          <li>Click <span class="emphasized">Data</span> tab.</li>
          <li>Click <span class="emphasized">Get Data</span> dropdown.</li>
          <li>Select <span class="emphasized">From File</span> > <span class="emphasized">From Text/CSV</span>.</li>
          <li>Select downloaded extract.</li>
          <li>Click <span class="emphasized">Import</span>.</li>
          <li>
            Update import settings:
            <ul>
              <li>File Origin = 65001: Unicode (UTF-8)</li>
              <li>Delimiter = Comma</li>
            </ul>
          </li>
          <li>Click <span class="emphasized">Transform Data</span>.</li>
          <li>Click <span class="emphasized">Close & Load</span>.</li>
          <li>CSV file has been imported.</li>
        </ol>
        <h3 class="mt-4 mb-2 primary--text">Importing CSV File into Excel (Mac)</h3>
        <ol>
          <li>Open a NEW Excel spreadsheet.</li>
          <li>Click <span class="emphasized">Data</span> tab.</li>
          <li>Click on <span class="emphasized">Get Data</span>.</li>
          <li>Select <span class="emphasized">From Text</span>.</li>
          <li>Select downloaded extract.</li>
          <li>Click <span class="emphasized">Get Data</span>. (This opens the Text Import Wizard.)</li>
          <li>
            Update import settings:
            <ul>
              <li>Data Type = Delimited</li>
              <li>File Origin = Unicode (UTF-8)</li>
              <li>Delimiter = Comma</li>
            </ul>
          </li>
          <li>Click <span class="emphasized">Next</span>.</li>
          <li>Select <span class="emphasized">Comma</span> as the only delimiter.</li>
          <li>Set Text Qualifier to double quotes (").</li>
          <li>Click <span class="emphasized">Next</span>.</li>
          <li>Set Column data format to <span class="emphasized">General</span>.</li>
          <li>Click <span class="emphasized">Finish</span>.</li>
          <li>CSV file has been imported.</li>
        </ol>

        <h3 class="mt-4 mb-2 primary--text">Converting Link Columns to Clickable Hyperlinks (Windows 11)</h3>
        <ol>
          <li>
            Identify the column containing the URLs (e.g., <span class="emphasized">Release Note Link</span> or
            <span class="emphasized">UAT Jira Link</span>).
          </li>
          <li>Click into the <span class="emphasized">first data cell</span> in the column.</li>
          <li>
            <span class="emphasized">Double-click the cell</span> to enter edit mode.
            <ul>
              <li>Excel may open the link in a browser—this is expected.</li>
            </ul>
          </li>
          <li>Click <span class="emphasized">any other cell</span> in the spreadsheet to exit edit mode.</li>
          <li>The original cell is now a <span class="emphasized">clickable hyperlink</span>.</li>
          <li>With that cell selected, click <span class="emphasized">Format Painter</span>.</li>
          <li>Select the remaining cells in the column to apply hyperlink formatting.</li>
          <li>Repeat for additional link columns as needed.</li>
        </ol>
      </v-card-text>
      <v-card-text> </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  computed: {
    extractTypeList() {
      return [
        {
          id: "memberPackSummary",
          name: "Pack Summary (CSV)",
          permission: this.hasAdminOrganization,
        },
        {
          id: "memberPackSummaryExcel",
          name: "Pack Summary (Excel)",
          permission: this.hasAdminOrganization,
        },
        {
          id: "packSummary",
          name: "Pack Summary (CSV)",
          permission: this.hasAdminOchin,
        },
        {
          id: "packSummaryExcel",
          name: "Pack Summary (Excel)",
          permission: this.hasAdminOchin,
        },
        {
          id: "uatProgressionStatus",
          name: "UAT Progression Status (CSV)",
          permission: this.hasAdminOchin,
        },
      ];
    },
    filteredExtractTypeList() {
      const extractListByUserPermission = this.extractTypeList.filter((extractType) => {
        return extractType.permission === true;
      });
      return extractListByUserPermission;
    },
    ...mapGetters({
      hasAdminOchin: "session/hasAdminOchin",
      hasAdminOrganization: "session/hasAdminOrganization",
    }),
    ...mapState({
      packList: (state) => state.pack.packList,
    }),
  },
  async created() {
    if (!this.packList.length) {
      await this.getPackList();
    }
  },
  data() {
    return {
      extractType: null,
      packId: null,
    };
  },
  methods: {
    ...mapActions({
      getPackList: "pack/getList",
    }),
  },
};
</script>
<style scoped>
.emphasized {
  font-weight: bold;
  font-style: italic;
}
</style>
